import React, { useEffect, useRef, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
} from "react-table";
import SkillService from "../Service/SkillService";

function SkillManagementPage() {
  const location = useLocation();
  const { user } = location.state || {};

  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(user.track);
  const [formData, setFormData] = useState({ skillId: null, skillName: "", track: user.track });
  const [isEditMode, setIsEditMode] = useState(false);
  const [search, setSearch] = useState("");
  const [newlyAddedSkillId, setNewlyAddedSkillId] = useState(null);
  const [manualPageIndex, setManualPageIndex] = useState(0);
  const [skillMatrixMessage, setSkillMatrixMessage] = useState("");
  const [isMatrixUploading, setIsMatrixUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [skillMasterMessage, setSkillMasterMessage] = useState("");
  const [isMasterUploading, setIsMasterUploading] = useState(false);
  const [selectedFile2, setSelectedFile2] = useState(null);
  const fileInputRef2 = useRef(null);
  const STATUS_MESSAGES = {
    noFile: "Please select a file before uploading.",
    invalidFileType: "Invalid file type. Please select an .xlsx file.",
    uploadFailed: "Upload failed.",
    exportFailed: "Export failed.",
    trackMissing: "Track is missing. Unable to proceed.",
    matrixUploadSuccess: "Skill Matrix uploaded successfully.",
    masterUploadSuccess: "Skill Master uploaded successfully.",
    matrixTemplateDownloadFailed: "Downloading Skill Matrix template failed.",
    masterTemplateDownloadFailed: "Downloading Skill Master template failed.",
    statusPlaceholder: "No recent action.",
    matrixUploadInProgress: "Uploading Skill Matrix...",
    masterUploadInProgress: "Uploading Skill Master...",
    supportedFormat: "Supported format: .xlsx",
  };


  useEffect(() => {
    fetchAllTracks();
  }, []);

  useEffect(() => {
    setManualPageIndex(0); // reset to first page on track change
    fetchSkillsByTrack(selectedTrack);
  }, [selectedTrack]);

  const fetchAllTracks = async () => {
    try {
      const response = await SkillService.getAllTracks();
      setTracks(response);
    } catch (err) {
      console.error("Error fetching tracks:", err);
      alert("Could not load tracks.");
    }
  };

  const fetchSkillsByTrack = async (track) => {
    try {
      setLoading(true);
      const response = await SkillService.getAllSkillsByTrack(track);
      setSkills(response);
    } catch (error) {
      console.error("Error fetching skills:", error);
      alert("Failed to fetch skills.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, skillName: e.target.value });
  };

  const handleTrackChange = (e) => {
    const track = e.target.value;
    setSelectedTrack(track);
    if (!isEditMode) {
      setFormData((prev) => ({ ...prev, track }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.skillName.trim()) {
      alert("Skill name is required.");
      return;
    }

    try {
      if (isEditMode) {
        await SkillService.updateSkill(formData);
        alert("Skill updated successfully.");

        // Update skill locally
        setSkills((prev) =>
          prev.map((skill) =>
            skill.skillId === formData.skillId ? { ...skill, ...formData } : skill
          )
        );
      } else {
        const savedSkill = await SkillService.addSkill({
          skillName: formData.skillName,
          track: selectedTrack,
        });
        alert("Skill added successfully.");
        await fetchSkillsByTrack(selectedTrack);
        setNewlyAddedSkillId(savedSkill.skillId);
      }

      setFormData({ skillId: null, skillName: "", track: selectedTrack });
      setIsEditMode(false);
    } catch (error) {
      console.error("Error saving skill:", error);
      alert("Failed to save skill.");
    }
  };

  const handleEdit = (skill) => {
    setFormData(skill);
    setSelectedTrack(skill.track);
    setIsEditMode(true);
  };

  const handleDelete = async (skillId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this skill?");
    if (!confirmDelete) return;

    try {
      await SkillService.deleteSkill(skillId);
      alert("Skill deleted successfully.");

      setSkills((prevSkills) => {
        const updatedSkills = prevSkills.filter((skill) => skill.skillId !== skillId);
        const filtered = updatedSkills.filter((s) =>
          s.skillName?.toLowerCase().includes(search.toLowerCase())
        );
        const totalPages = Math.ceil(filtered.length / pageSize);
        const newPageIndex = pageIndex >= totalPages && totalPages > 0 ? totalPages - 1 : pageIndex;
        setManualPageIndex(newPageIndex);
        gotoPage(newPageIndex);
        return updatedSkills;
      });
    } catch (error) {
      alert(error.response?.data?.message || "Skill could not be deleted.");
    }
  };

  const filteredSkills = useMemo(() => {
    return skills.filter((s) =>
      s.skillName?.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, skills]);

  const columns = useMemo(
    () => [
      { Header: "Skill Id", accessor: "skillId" },
      { Header: "Skill Name", accessor: "skillName" },
      { Header: "Track", accessor: "track" },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="space-x-2">
            <button
              onClick={() => handleEdit(row.original)}
              className="bg-yellow-400 px-3 py-1 rounded hover:bg-yellow-500"
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(row.original.skillId)}
              className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            >
              Delete
            </button>
          </div>
        ),
        disableSortBy: true,
        disableFilters: true,
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredSkills,
      initialState: { pageIndex: manualPageIndex, pageSize: 5 },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    if (newlyAddedSkillId && filteredSkills.length > 0) {
      const index = filteredSkills.findIndex(skill => skill.skillId === newlyAddedSkillId);
      if (index !== -1) {
        const targetPage = Math.floor(index / pageSize);
        setManualPageIndex(targetPage);
        gotoPage(targetPage);
      }
      setNewlyAddedSkillId(null);
    }
  }, [filteredSkills, newlyAddedSkillId, pageSize, gotoPage]);

  const handleGotoPage = (pageNumber) => {
    setManualPageIndex(pageNumber);
    gotoPage(pageNumber);
  };

  const triggerFileDownload = (response, fallbackFileName) => {
    const contentDisposition = response?.headers?.["content-disposition"] || "";
    const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    const fileName = fileNameMatch?.[1] || fallbackFileName;

    const fileUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(fileUrl);

    return fileName;
  };

  const refreshSkills = async (track) => {
    if (!track) return;
    await fetchSkillsByTrack(track);
  };

  const isExcelFile = (file) => {
    if (!file?.name) return false;
    return file.name.toLowerCase().endsWith(".xlsx");
  };

  const getStatusClassName = (message) => {
    if (!message) return "text-gray-400";
    if (/failed|missing|please select|invalid/i.test(message)) return "text-red-600";
    if (/selected|uploading/i.test(message)) return "text-blue-600";
    return "text-green-600";
  };

  const handleSkillMatrixFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!isExcelFile(file)) {
        setSelectedFile(null);
        setSkillMatrixMessage(STATUS_MESSAGES.invalidFileType);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setSelectedFile(file);
      setSkillMatrixMessage(`File selected: ${file.name}`);
    }
  };

  const handleSkillMatrixFileUpload = async () => {
    if (!selectedFile) {
      setSkillMatrixMessage(STATUS_MESSAGES.noFile);
      return;
    }
    const activeTrack = selectedTrack || user?.track;
    if (!activeTrack) {
      setSkillMatrixMessage(STATUS_MESSAGES.trackMissing);
      return;
    }
    const formData = new FormData();
    formData.append("formData", selectedFile);
    setIsMatrixUploading(true);
    setSkillMatrixMessage(STATUS_MESSAGES.matrixUploadInProgress);
    try {
      const response = await SkillService.skillMatrixImport(formData, activeTrack);
      if (response != null) {
        setSelectedFile(null);
        setSkillMatrixMessage(STATUS_MESSAGES.matrixUploadSuccess);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        await refreshSkills(activeTrack);
      }
    } catch (error) {
      console.error("Error uploading skill matrix file:", error);
      setSkillMatrixMessage(STATUS_MESSAGES.uploadFailed);
    } finally {
      setIsMatrixUploading(false);
    }
  };

  const handleSkillMatrixExport = async () => {
    try {
      const activeTrack = selectedTrack || user?.track;
      if (!activeTrack) {
        setSkillMatrixMessage(STATUS_MESSAGES.trackMissing);
        return;
      }
      const response = await SkillService.skillMatrixExport(activeTrack);
      const fileName = triggerFileDownload(response, `skill matrix-${activeTrack}.xlsx`);
      setSkillMatrixMessage(`${fileName} downloaded successfully.`);
    } catch (error) {
      console.error("Error exporting skill matrix:", error);
      setSkillMatrixMessage(STATUS_MESSAGES.exportFailed);
    }
  };

  const handleSkillMasterFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!isExcelFile(file)) {
        setSelectedFile2(null);
        setSkillMasterMessage(STATUS_MESSAGES.invalidFileType);
        if (fileInputRef2.current) {
          fileInputRef2.current.value = "";
        }
        return;
      }
      setSelectedFile2(file);
      setSkillMasterMessage(`File selected: ${file.name}`);
    }
  };

  const handleSkillMasterFileUpload = async () => {
    if (!selectedFile2) {
      setSkillMasterMessage(STATUS_MESSAGES.noFile);
      return;
    }
    const activeTrack = selectedTrack || user?.track;
    if (!activeTrack) {
      setSkillMasterMessage(STATUS_MESSAGES.trackMissing);
      return;
    }

    const formData = new FormData();
    formData.append("formData", selectedFile2);
    setIsMasterUploading(true);
    setSkillMasterMessage(STATUS_MESSAGES.masterUploadInProgress);
    try {
      const response = await SkillService.skillMasterImport(formData, activeTrack);
      if (response != null) {
        setSelectedFile2(null);
        setSkillMasterMessage(STATUS_MESSAGES.masterUploadSuccess);
        if (fileInputRef2.current) {
          fileInputRef2.current.value = "";
        }
        await refreshSkills(activeTrack);
      }
    } catch (error) {
      console.error("Error uploading skill master file:", error);
      setSkillMasterMessage(STATUS_MESSAGES.uploadFailed);
    } finally {
      setIsMasterUploading(false);
    }
  };

  const handleSkillMasterExport = async () => {
    try {
      const activeTrack = selectedTrack || user?.track;
      if (!activeTrack) {
        setSkillMasterMessage(STATUS_MESSAGES.trackMissing);
        return;
      }
      const response = await SkillService.skillMasterExport(activeTrack);
      const fileName = triggerFileDownload(response, `skill master-${activeTrack}.xlsx`);
      setSkillMasterMessage(`${fileName} downloaded successfully.`);
    } catch (error) {
      console.error("Error exporting skill master:", error);
      setSkillMasterMessage(STATUS_MESSAGES.exportFailed);
    }
  };

  const handleDownloadSkillMatrixTemplate = async () => {
    try {
      const response = await SkillService.downloadSkillMatrixTemplate();
      const fileName = triggerFileDownload(response, "SkillMatrixTemplate.xlsx");
      setSkillMatrixMessage(`${fileName} downloaded successfully.`);
    } catch (error) {
      console.error("Error downloading skill matrix template:", error);
      setSkillMatrixMessage(STATUS_MESSAGES.matrixTemplateDownloadFailed);
    }
  };

  const handleDownloadSkillMasterTemplate = async () => {
    try {
      const response = await SkillService.downloadSkillMasterTemplate();
      const fileName = triggerFileDownload(response, "SkillMasterTemplate.xlsx");
      setSkillMasterMessage(`${fileName} downloaded successfully.`);
    } catch (error) {
      console.error("Error downloading skill master template:", error);
      setSkillMasterMessage(STATUS_MESSAGES.masterTemplateDownloadFailed);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4 text-center">Skill Management</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-start">
        <div className="w-full bg-gray-100 rounded-lg shadow-lg p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="inline-flex items-center rounded-full bg-gray-200 px-3 py-1 text-sm font-semibold text-gray-700">
              Skill Master
            </h4>
            <span className="text-xs text-gray-500">{STATUS_MESSAGES.supportedFormat}</span>
          </div>
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef2}
            onChange={handleSkillMasterFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <button
              type="button"
              onClick={handleSkillMasterFileUpload}
              disabled={isMasterUploading || !selectedFile2}
              className="inline-flex w-auto min-w-[170px] justify-center px-4 py-2 text-sm font-semibold text-white bg-blue-500 rounded-lg shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isMasterUploading ? "Uploading..." : "Upload File"}
            </button>
            <button
              type="button"
              className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
              onClick={handleSkillMasterExport}
            >
              Export Data
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
              onClick={handleDownloadSkillMasterTemplate}
            >
              Download Template
            </button>
          </div>
          <p className={`mt-3 min-h-[20px] text-sm font-medium ${getStatusClassName(skillMasterMessage)}`}>
            {skillMasterMessage || STATUS_MESSAGES.statusPlaceholder}
          </p>
        </div>

        <div className="w-full bg-gray-100 rounded-lg shadow-lg p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="inline-flex items-center rounded-full bg-gray-200 px-3 py-1 text-sm font-semibold text-gray-700">
              Skill Matrix
            </h4>
            <span className="text-xs text-gray-500">{STATUS_MESSAGES.supportedFormat}</span>
          </div>
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            onChange={handleSkillMatrixFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <button
              type="button"
              onClick={handleSkillMatrixFileUpload}
              disabled={isMatrixUploading || !selectedFile}
              className="inline-flex w-auto min-w-[170px] justify-center px-4 py-2 text-sm font-semibold text-white bg-blue-500 rounded-lg shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isMatrixUploading ? "Uploading..." : "Upload File"}
            </button>
            <button
              type="button"
              className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
              onClick={handleSkillMatrixExport}
            >
              Export Data
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
              onClick={handleDownloadSkillMatrixTemplate}
            >
              Download Template
            </button>
          </div>
          <p className={`mt-3 min-h-[20px] text-sm font-medium ${getStatusClassName(skillMatrixMessage)}`}>
            {skillMatrixMessage || STATUS_MESSAGES.statusPlaceholder}
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col md:flex-row items-center gap-4 mb-6">
        <select
          name="track"
          value={selectedTrack}
          onChange={handleTrackChange}
          disabled={isEditMode}
          className="w-40 px-3 py-2 border rounded"
        >
          <option value="" disabled>Select Track</option>
          {tracks.map((track) => (
            <option key={track} value={track}>
              {track}
            </option>
          ))}
        </select>

        <input
          type="text"
          name="skillName"
          placeholder="Enter skill name"
          value={formData.skillName}
          onChange={handleInputChange}
          className="flex-1 px-3 py-2 border rounded"
        />

        <button
          type="submit"
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          {isEditMode ? "Update Skill" : "Add Skill"}
        </button>

        {isEditMode && (
          <button
            type="button"
            onClick={() => {
              setIsEditMode(false);
              setFormData({ skillId: null, skillName: "", track: selectedTrack });
              fetchSkillsByTrack(selectedTrack);
            }}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
        )}
      </form>

      <input
        type="text"
        placeholder="Search by skill name..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full mb-4 px-4 py-2 border border-gray-300 rounded"
      />

      <table {...getTableProps()} className="min-w-full bg-white border rounded shadow-sm">
        <thead className="bg-gray-100">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.id !== "Actions" ? column.getSortByToggleProps() : {})}
                  className="text-left px-4 py-2 border-b"
                >
                  {column.render("Header")}
                  {column.id !== "Actions" && (
                    <span className="ml-1 text-gray-400">
                      {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : "⬍"}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} className="hover:bg-gray-50">
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} className="px-4 py-2 border-b">
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-4">
        <div className="flex gap-2">
          <button
            onClick={() => handleGotoPage(pageIndex - 1)}
            disabled={!canPreviousPage}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => handleGotoPage(pageIndex + 1)}
            disabled={!canNextPage}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="text-sm">
          Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
        </div>
        <div>
          Go to page:{" "}
          <input
            type="number"
            min={1}
            max={pageOptions.length}
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
              handleGotoPage(pageNumber);
            }}
            className="w-16 px-2 py-1 border rounded"
          />
        </div>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="px-2 py-1 border rounded"
        >
          {[5, 10, 20].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default SkillManagementPage;
